import { useMemo } from 'react';
import {
  apiURL,
  BASIC_AUTH_PASSWORD,
  BASIC_AUTH_USERNAME,
} from '../environment/environment';
import { CompanyResponse } from '../models/CompaniesModel';
import {
  Category,
  IQuestion,
  PopulatedTemplate,
  QuestionPayload,
  TemplatePayload,
} from '../models/SurveyModels';
import { PopulatedResource, ResourcePayload } from '../models/LibraryModels';
import { IssuePayload, PopulatedIssue } from '../models/IssueModels';

const basicAuth = btoa(`${BASIC_AUTH_USERNAME}:${BASIC_AUTH_PASSWORD}`);

export function objectToQueryParams(obj: any) {
  const params = new URLSearchParams();
  for (let key in obj) {
    if (obj[key]) {
      params.append(key, obj[key]);
    }
  }
  return params.toString();
}

export const useEndpoints = () =>
  useMemo(
    () =>
      ({
        //AUTH
        login: (body: Record<'email' | 'password', string>) => {
          const requestOption = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Basic ${basicAuth}`,
            },
            body: JSON.stringify({
              username: body.email,
              password: body.password,
            }),
          };
          return fetch(`${apiURL}/auth/login`, requestOption);
        },
        resetPassword: (queryparams: { email: string }) => {
          const requestOption = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Basic ${basicAuth}`,
            },
          };
          return fetch(
            `${apiURL}/auth/password-request?${objectToQueryParams(
              queryparams
            )}`,
            requestOption
          );
        },
        me: () => {
          const requestOption = {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          };
          return fetch(`${apiURL}/auth/me`, requestOption);
        },

        getAllCompanies: async (queryParams?: {
          name?: string;
          enabled: boolean | string;
          limit?: number;
          skip?: number;
        }): Promise<{
          count: number;
          items: CompanyResponse[];
        }> => {
          const requestOption = {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          };

          const response = await fetch(
            `${apiURL}/companies?${objectToQueryParams(queryParams)}`,
            requestOption
          );
          return response.json();
        },

        createCompany: (body: CompanyResponse) => {
          const requestOption = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: body.name,
              headOfficeCity: body.headOfficeCity,
              headOfficeRegion: body.headOfficeRegion,
              vatNumber: body.vatNumber,
              nOfEmployees: body.nOfEmployees,
              businessSector: body.businessSector,
              otherOfficies: body.otherOfficies,
              hrName: body.hrName,
              hrSurname: body.hrSurname,
              hrEmail: body.hrEmail,
              hrPhone: body.hrPhone,
              hrLanguage: body.hrLanguage,
            }),
          };
          return fetch(`${apiURL}/companies`, requestOption);
        },

        getCompanyById: async (id: string) => {
          const requestOption = {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          };
          const response = await fetch(
            `${apiURL}/companies/${id}`,
            requestOption
          );
          return response.json();
        },

        putCompany: (id: string, body: CompanyResponse) => {
          const requestOption = {
            method: 'PUT',
            body: JSON.stringify({
              name: body.name,
              headOfficeCity: body.headOfficeCity,
              headOfficeRegion: body.headOfficeRegion,
              vatNumber: body.vatNumber,
              nOfEmployees: body.nOfEmployees,
              businessSector: body.businessSector,
              otherOfficies: body.otherOfficies,
              // Referente HR — campi sbloccati nel form, propagati al User
              hrName: body.hrName,
              hrSurname: body.hrSurname,
              hrEmail: body.hrEmail,
              hrPhone: body.hrPhone,
              hrLanguage: body.hrLanguage,
              // Canone
              subscriptions: body.subscriptions,
              subscriptionCcEmails: body.subscriptionCcEmails,
            }),
          };

          return fetch(`${apiURL}/companies/${id}`, requestOption);
        },

        exportAllSurveys: (filters?: {
          company_ids?: string;
          survey_ids?: string;
          survey_name?: string;
          date_from?: string;
          date_to?: string;
          question_text?: string;
          category_ids?: string;
        }) => {
          const requestOption = {
            method: 'GET',
          };
          const qs = filters ? objectToQueryParams(filters) : '';
          const url = qs
            ? `${apiURL}/companies/export-surveys?${qs}`
            : `${apiURL}/companies/export-surveys`;
          return fetch(url, requestOption);
        },

        exportCompaniesAnagrafica: (filters?: { company_ids?: string }) => {
          const requestOption = {
            method: 'GET',
          };
          const qs = filters ? objectToQueryParams(filters) : '';
          const url = qs
            ? `${apiURL}/companies/export-companies?${qs}`
            : `${apiURL}/companies/export-companies`;
          return fetch(url, requestOption);
        },

        patchCompany: (
          id: string,
          body: { enabled?: boolean; libraryEnabled?: boolean }
        ) => {
          const requestOption = {
            method: 'PATCH',
            body: JSON.stringify({
              enabled: body.enabled,
              libraryEnabled: body.libraryEnabled,
            }),
          };
          return fetch(`${apiURL}/companies/${id}`, requestOption);
        },

        getCategories: async (queryParams?: {
          language?: string;
          coreArea?: string;
          type?: string;
        }): Promise<Category[]> => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(
            `${apiURL}/categories?${objectToQueryParams(queryParams)}`,
            requestOption
          );
          return response.json();
        },

        getAllQuestions: async (queryParams: {
          enabled?: string;
          category?: string;
          text?: string;
          coreArea?: string;
          limit?: number;
          role?: string;
          skip?: number;
        }): Promise<{
          count: number;
          limit: number;
          items: IQuestion[];
        }> => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(
            `${apiURL}/questions?${objectToQueryParams(queryParams)}`,
            requestOption
          );
          return response.json();
        },
        createQuestion: (body: QuestionPayload) => {
          const requestOption = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              translations: body.translations,
              answerMode: body.answerMode,
              categoryIds: body.categoryIds,
              type: body.type,
              actor: body.actor,
              self: body.self,
            }),
          };
          return fetch(`${apiURL}/questions`, requestOption);
        },
        getQuestionbyId: async (id: string) => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(
            `${apiURL}/questions/${id}`,
            requestOption
          );
          return response.json();
        },
        putQuestion: (id: string, body: QuestionPayload) => {
          const requestOption = {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              translations: body.translations,
              answerMode: body.answerMode,
              categoryIds: body.categoryIds,
              type: body.type,
              actor: body.actor,
              self: body.self,
            }),
          };
          return fetch(`${apiURL}/questions/${id}`, requestOption);
        },
        patchQuestion: (id: string) => {
          const requestOption = {
            method: 'PATCH',
          };
          return fetch(`${apiURL}/questions/${id}`, requestOption);
        },

        exportTemplates: (queryParams?: { status?: string; name?: string }) => {
          const qs = objectToQueryParams(queryParams);
          return fetch(`${apiURL}/templates/export${qs ? `?${qs}` : ''}`, {
            method: 'GET',
          });
        },
        exportQuestions: (queryParams?: {
          enabled?: string;
          category?: string;
          coreArea?: string;
          text?: string;
        }) => {
          const qs = objectToQueryParams(queryParams);
          return fetch(`${apiURL}/questions/export${qs ? `?${qs}` : ''}`, {
            method: 'GET',
          });
        },
        getAllTemplate: async (queryParams: {
          status?: string;
          name?: string;
        }): Promise<PopulatedTemplate[]> => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(
            `${apiURL}/templates?${objectToQueryParams(queryParams)}`,
            requestOption
          );
          return response.json();
        },
        patchTemplate: (
          id: string,
          status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED'
        ) => {
          const requestOption = {
            method: 'PATCH',
            body: JSON.stringify({
              status: status,
            }),
          };
          return fetch(`${apiURL}/templates/${id}`, requestOption);
        },
        getTemplatebyId: async (id: string) => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(
            `${apiURL}/templates/${id}`,
            requestOption
          );
          return response.json();
        },
        putTemplate: (id: string, body: TemplatePayload) => {
          const requestOption = {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              translations: body.translations,
              questionIds: body.questions.map(({ id }) => id),
              image: body.image,
            }),
          };
          return fetch(`${apiURL}/templates/${id}`, requestOption);
        },
        createTemplate: (body: TemplatePayload) => {
          const requestOption = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              translations: body.translations,
              questionIds: body.questions.map(({ id }) => id),
              image: body.image,
            }),
          };
          return fetch(`${apiURL}/templates`, requestOption);
        },

        /** Library */
        getAllLibraries: async (queryParams?: {
          type?: string;
          category?: string;
          role?: string;
          title?: string;
        }): Promise<PopulatedResource[]> => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(
            `${apiURL}/resources/all?${objectToQueryParams(queryParams)}`,
            requestOption
          );
          return response.json();
        },
        createLibrary: (body: ResourcePayload) => {
          const requestOption = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              translations: body.translations,
              categoryId: body.categoryId,
              type: body.type,
              roles: body.roles,
            }),
          };
          return fetch(`${apiURL}/resources`, requestOption);
        },
        getLibrarybyId: async (id: string) => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(
            `${apiURL}/resources/${id}`,
            requestOption
          );
          return response.json();
        },
        putLibrary: (id: string, body: ResourcePayload) => {
          const requestOption = {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              translations: body.translations,
              categoryId: body.categoryId,
              type: body.type,
              roles: body.roles,
            }),
          };
          return fetch(`${apiURL}/resources/${id}`, requestOption);
        },
        patchLibrary: (id: string) => {
          const requestOption = {
            method: 'PATCH',
          };
          return fetch(`${apiURL}/resources/${id}/status`, requestOption);
        },
        /* Issue */
        getAllIssue: async (queryParams: {
          category?: string;
          status?: string;
          name?: string;
          limit?: number;
          skip?: number;
          severity?: string;
        }): Promise<PopulatedIssue[]> => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(
            `${apiURL}/issues?${objectToQueryParams(queryParams)}`,
            requestOption
          );
          return response.json();
        },
        createIssue: (body: IssuePayload) => {
          const requestOption = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              role: body.role,
              translations: body.translations,
              categoryId: body.categoryId,
              severity: body.severity,
              questionIds: body.questions?.map(({ id }) => id),
              resourceId: body.resourceId?.id,
            }),
          };
          return fetch(`${apiURL}/issues`, requestOption);
        },
        getIssuebyId: async (id: string) => {
          const requestOption = {
            method: 'GET',
          };
          const response = await fetch(`${apiURL}/issues/${id}`, requestOption);
          return response.json();
        },
        putIssue: (id: string, body: IssuePayload) => {
          const requestOption = {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              role: body.role,
              translations: body.translations,
              categoryId: body.categoryId,
              severity: body.severity,
              questionIds: body.questions?.map(({ id }) => id),
              resourceId: body.resourceId?.id,
            }),
          };
          return fetch(`${apiURL}/issues/${id}`, requestOption);
        },
        patchIssue: (id: string) => {
          const requestOption = {
            method: 'PATCH',
          };
          return fetch(`${apiURL}/issues/${id}`, requestOption);
        },
      } as const),
    []
  );

export type Endpoints = ReturnType<typeof useEndpoints>;
